import { IconButton, InputAdornment } from '@mui/material';
import { ChangeEvent, FocusEvent, useState } from 'react';
import { Control, Controller } from 'react-hook-form';

import TextField, { StyledTextFieldProps } from '.';
import Visibility from '../../assets/icons/Visibility';
import VisibilityOff from '../../assets/icons/VisibilityOff';
import colors from '../../constants/colors';

export type ControlledTextFieldProps = StyledTextFieldProps & {
  name: string;
  control: Control<any>;
  showClearIfValue?: boolean;
  handleBlur?: (
    e: FocusEvent<HTMLInputElement>,
    onBlur: (e: FocusEvent<HTMLInputElement>) => void,
  ) => void;
  handleChange?: (
    e: ChangeEvent<HTMLInputElement>,
    onChange: (e: ChangeEvent<HTMLInputElement>) => void,
  ) => void;
  hideErrorMessage?: boolean;
  preventWhitespace?: boolean;
  preventNonNumericValues?: boolean;
  preventNonAlphaNumericValues?: boolean;
  validateCustomerNameInput?: boolean;
  showVisibilityToggle?: boolean;
};

const ControlledTextField = ({
  name,
  control,
  helperText,
  showClearIfValue,
  handleBlur,
  handleChange,
  hideErrorMessage,
  inputProps,
  preventWhitespace,
  preventNonNumericValues,
  preventNonAlphaNumericValues,
  validateCustomerNameInput,
  showVisibilityToggle,
  ...props
}: ControlledTextFieldProps) => {
  const [showFieldValue, setShowFieldValue] = useState(false);

  const getHelperText = (error?: { message?: string }) => {
    if (hideErrorMessage) {
      return null;
    } else {
      return error?.message ?? helperText;
    }
  };

  const getInputType = () => {
    if (showVisibilityToggle) {
      return showFieldValue ? 'text' : 'password';
    }
    return props.type;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { onBlur, onChange, value },
        fieldState: { error },
      }) => (
        <TextField
          name={name}
          helperText={getHelperText(error)}
          fullWidth
          onBlur={(e) => {
            if (showVisibilityToggle && showFieldValue) {
              setShowFieldValue(false);
            }

            if (handleBlur) {
              // @ts-ignore
              handleBlur(e as FocusEvent<HTMLInputElement>, onBlur);
            } else {
              // @ts-ignore
              onBlur(e);
            }
          }}
          onChange={(e) => {
            if (preventWhitespace) {
              const { value } = e.target;
              const formattedValue = value.replace(/\s/g, '');
              e.target.value = formattedValue;
            }

            if (preventNonAlphaNumericValues) {
              const { value } = e.target;
              const formattedValue = value.replace(/[\W_]+/g, '');
              e.target.value = formattedValue;
            }

            if (preventNonNumericValues) {
              const { value } = e.target;
              const formattedValue = value.replace(/[^0-9]/g, '');
              e.target.value = formattedValue;
            }

            if (validateCustomerNameInput) {
              const { value } = e.target;
              const formattedValue = value.replace(/[^a-z A-Z'-]/g, '');
              e.target.value = formattedValue;
            }

            if (handleChange) {
              handleChange(e as ChangeEvent<HTMLInputElement>, onChange);
            } else {
              onChange(e);
            }
          }}
          value={value ?? ''}
          error={!!error?.message}
          showClear={showClearIfValue && value}
          inputProps={{
            ...(preventWhitespace && {
              onKeyPress: (event: any) => {
                if (event.code === 'Space') {
                  event.preventDefault();
                }
              },
            }),
            ...inputProps,
          }}
          InputProps={{
            ...(showVisibilityToggle && {
              required: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showFieldValue ? 'Show password' : 'Hide password'
                    }
                    disableRipple
                    onClick={() => setShowFieldValue((val) => !val)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    sx={{
                      p: 0,
                      '&:hover': {
                        backgroundColor: `${colors.textFieldHoverBackground}`,
                        outline: `solid 2px ${colors.textFieldHoverBackground}`,
                        transition: 'none',
                      },
                    }}
                  >
                    {showFieldValue ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }),
            sx: { background: colors.csaaWhite },
          }}
          InputLabelProps={{
            disabled: false,
          }} /** when disabled the label color is muted, but this causes color contrast issues  */
          type={getInputType()}
          {...props}
        />
      )}
    />
  );
};

export default ControlledTextField;
